import React, { useState, useEffect } from 'react';
import confetti from 'canvas-confetti';
import { Button } from 'react-bootstrap';

// Rainbow colors in order
const rainbowColors = [
  { name: 'Red', color: '#ff0000', emoji: '❤️' },
  { name: 'Orange', color: '#ff7f00', emoji: '🧡' },
  { name: 'Yellow', color: '#ffff00', emoji: '💛' },
  { name: 'Green', color: '#00ff00', emoji: '💚' },
  { name: 'Blue', color: '#0000ff', emoji: '💙' },
  { name: 'Indigo', color: '#4b0082', emoji: '💜' },
  { name: 'Violet', color: '#9400d3', emoji: '💜' }
];

export const RainbowColorGame = ({ handleClose, onGameComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedColors, setCompletedColors] = useState([]);
  const [shuffledColors, setShuffledColors] = useState([]);
  const [showCelebration, setShowCelebration] = useState(false);

  const correctSound = new Audio('/sounds/correct.mp3');
  const errorSound = new Audio('/sounds/error.mp3');
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);

  useEffect(() => {
    const savedVolume = localStorage.getItem('tapGameVolume');
    if (savedVolume !== null) {
      setVolume(parseFloat(savedVolume));
    }
    // Shuffle colors for selection
    setShuffledColors([...rainbowColors].sort(() => Math.random() - 0.5));
  }, []);

  // Speak instruction when current step changes
  useEffect(() => {
    const targetColor = rainbowColors[currentStep];
    if (targetColor && !isMuted && !showCelebration) {
      const speakTimeout = setTimeout(() => {
        const utterance = new SpeechSynthesisUtterance(
          `Tap ${targetColor.name}`
        );
        utterance.rate = 0.8;
        utterance.volume = volume;
        speechSynthesis.speak(utterance);
      }, 600);

      return () => clearTimeout(speakTimeout);
    }
  }, [currentStep, isMuted, volume, showCelebration]);

  const handleColorClick = (color) => {
    if (color.name === rainbowColors[currentStep].name) {
      if (!isMuted) {
        correctSound.volume = volume;
        correctSound.play();
      }
      
      setCompletedColors([...completedColors, color]);
      
      if (currentStep === rainbowColors.length - 1) {
        setShowCelebration(true);
        const stars = 3;
        const score = rainbowColors.length * 15;
        confetti({ particleCount: 250, spread: 100, origin: { y: 0.6 } });
        
        setTimeout(() => {
          onGameComplete?.(stars, score);
          handleClose?.();
        }, 3000);
      } else {
        setCurrentStep(currentStep + 1);
      }
    } else {
      if (!isMuted) {
        errorSound.volume = volume;
        errorSound.play();
      }
    }
  };

  return (
    <div className="tap-letter-board-bg">
      <div className="tap-header-bar mb-1">
        <div className="tap-score-box">
          <span role="img" aria-label="score" style={{ fontSize: 22, marginRight: 6 }}>🌈</span>
          <span className="tap-score-label">Progress</span>
          <span className="tap-score-value">{currentStep + 1}/{rainbowColors.length}</span>
        </div>
        <div className="tap-letter-target">
          <span className="tap-letter-label">Find:</span>
          <span className="tap-letter-value" style={{ color: rainbowColors[currentStep]?.color }}>
            {rainbowColors[currentStep]?.name}
          </span>
        </div>
        <div className="tap-volume-box">
          <span role="img" aria-label="volume" style={{ fontSize: 20, marginRight: 4 }}>🔊</span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={(e) => {
              const newVol = parseFloat(e.target.value);
              setVolume(newVol);
              localStorage.setItem('tapGameVolume', newVol);
            }}
            style={{ width: '90px', verticalAlign: 'middle' }}
          />
          <span className="tap-volume-value">{Math.round(volume * 100)}%</span>
        </div>
      </div>

      <div className="game-content-area">
        <div className="rainbow-display">
          {rainbowColors.map((color, index) => (
            <div
              key={color.name}
              className={`rainbow-stripe ${index <= currentStep ? 'filled' : ''}`}
              style={{ 
                backgroundColor: index < currentStep ? color.color : 
                               index === currentStep && completedColors.includes(color) ? color.color : '#e0e0e0'
              }}
            />
          ))}
        </div>

        {!showCelebration ? (
          <div className="tap-letter-btn-grid">
            {shuffledColors.map(color => (
              <button
                key={color.name}
                className={`btn m-1 letter-btn ${completedColors.includes(color) ? 'btn-success selected-correct' : 'btn-outline-primary'}`}
                onClick={() => handleColorClick(color)}
                disabled={completedColors.includes(color)}
                style={{
                  background: completedColors.includes(color) ? '#000' : `linear-gradient(135deg, ${color.color}22, ${color.color}44)`,
                  borderColor: color.color,
                  borderWidth: '3px',
                  minWidth: '100px',
                  minHeight: '80px'
                }}
              >
                <div style={{ fontSize: '2rem' }}>{color.emoji}</div>
                <div style={{ fontWeight: 600, color: completedColors.includes(color) ? '#fff' : '#333' }}>
                  {color.name}
                </div>
              </button>
            ))}
          </div>
        ) : (
          <div className="celebration">
            <h2>🎉 Perfect Rainbow! 🌈</h2>
          </div>
        )}
      </div>

      <style>{`
        .tap-letter-board-bg {
          background: url('/image/board.png'), linear-gradient(135deg,rgba(62, 62, 62, 0.3) 0%,rgba(15, 15, 14, 0.41) 100%);
          background-size: cover, cover;
          background-position: center, center;
          border-radius: 32px;
          box-shadow: 0 6px 32px #e0c68a33;
          padding: 32px 12px 24px 12px;
          margin: 0 auto;
          max-width: 900px;
          width: 98%;
          min-height: 400px;
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
          z-index: 2;
          animation: board-pop 0.5s cubic-bezier(.68,-0.55,.27,1.55);
        }
        @keyframes board-pop {
          0% { transform: scale(0.9); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
        .tap-header-bar {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-radius: 20px;
          box-shadow: 0 2px 8px rgba(224,198,138,0.10);
          padding: 1px 18px 18px 18px;
          margin-bottom: 18px;
          gap: 10px;
        }
        .tap-score-box,
        .tap-volume-box {
          display: flex;
          align-items: center;
          border-radius: 14px;
          padding: 6px 14px;
          font-weight: 700;
          font-size: 1.1rem;
          color: #b8860b;
          background: rgba(255, 255, 255, 0.86);
          min-width: 110px;
          box-shadow: 0 1px 4px rgba(224,198,138,0.10);
        }
        .tap-score-label {
          margin-right: 6px;
          font-size: 1rem;
          color: #b8860b;
        }
        .tap-score-value {
          font-size: 1.1rem;
          color: #1976d2;
          margin-left: 4px;
          font-weight: 700;
        }
        .tap-letter-target {
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(224,198,138,0.92);
          border-radius: 18px;
          padding: 8px 32px;
          box-shadow: 0 1px 4px rgba(224,198,138,0.10);
          margin: 0 18px;
          min-width: 180px;
          flex: 1;
        }
        .tap-letter-label {
          font-size: 1.1rem;
          color: #3e2723;
          margin-right: 8px;
          font-weight: 600;
        }
        .tap-letter-value {
          font-size: 1.8rem;
          font-weight: 900;
          text-shadow: 1px 1px 8px #fff, 0 0 8px #e0c68a;
        }
        .tap-volume-value {
          margin-left: 8px;
          font-size: 1rem;
          color: #1976d2;
          font-weight: 700;
          min-width: 36px;
          display: inline-block;
          text-align: right;
        }
        .game-content-area {
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .rainbow-display {
          display: flex;
          justify-content: center;
          gap: 0.4rem;
          margin: 1.5rem auto;
          height: 70px;
          max-width: 100%;
        }
        .rainbow-stripe {
          width: 45px;
          border-radius: 10px;
          transition: all 0.5s ease;
          box-shadow: 0 5px 15px rgba(0,0,0,0.2);
          flex-shrink: 0;
        }
        .rainbow-stripe.filled {
          animation: fillStripe 0.5s ease;
        }
        @keyframes fillStripe {
          0% { transform: scaleY(0); }
          100% { transform: scaleY(1); }
        }
        .tap-letter-btn-grid {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          align-items: center;
          width: 100%;
          margin-top: 10px;
          gap: 8px;
        }
        .letter-btn {
          padding: 8px;
          border-radius: 18px !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.10);
          transition: transform 0.1s, box-shadow 0.2s;
        }
        .letter-btn:hover {
          transform: translateY(-5px) scale(1.05);
        }
        .selected-correct {
          background: #000000 !important;
          border-color: rgb(0, 245, 253) !important;
          color: #fff !important;
          box-shadow: 0 0 16px 2px rgba(122, 255, 244, 0.6);
        }
        .celebration {
          text-align: center;
          animation: celebrate 1s ease infinite;
          padding: 2rem;
        }
        @keyframes celebrate {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
        @media (max-width: 700px) {
          .tap-header-bar {
            flex-direction: column;
            gap: 10px;
            padding-bottom: 10px;
          }
          .tap-letter-target {
            min-width: 0;
            width: 100%;
            padding: 8px 0;
            margin: 8px 0;
          }
          .rainbow-display {
            height: 60px;
            gap: 0.3rem;
          }
          .rainbow-stripe {
            width: 35px;
          }
          .letter-btn {
            min-width: 80px !important;
            min-height: 60px !important;
          }
        }
        @media (max-width: 480px) {
          .tap-letter-board-bg {
            padding: 10px;
          }
          .rainbow-display {
            height: 50px;
          }
          .rainbow-stripe {
            width: 28px;
          }
          .letter-btn {
            min-width: 70px !important;
            min-height: 50px !important;
          }
        }
      `}</style>
    </div>
  );
};